/**
 * VideoPlayerContext
 * 
 * Global context for managing shared HLS instances across the app.
 * Similar to Flutter's VideoPlayerController management.
 * 
 * This allows:
 * - Sharing HLS instances between different video elements
 * - Seamless transitions (list → fullscreen → back)
 * - Preserved buffers and playback position
 * - Efficient memory management
 */

import React, { createContext, useContext, useRef, useCallback } from 'react';
import type Hls from 'hls.js';

interface VideoPlayerContextValue {
  /**
   * Get HLS instance for a video
   */
  getHlsInstance: (videoKey: string) => Hls | null;

  /**
   * Store HLS instance for a video
   */
  setHlsInstance: (videoKey: string, hls: Hls | null) => void;

  /**
   * Remove and destroy HLS instance
   */
  destroyHlsInstance: (videoKey: string) => void;

  /**
   * Clear all HLS instances (e.g., on logout)
   */
  clearAllHlsInstances: () => void;
}

const VideoPlayerContext = createContext<VideoPlayerContextValue | null>(null);

export const VideoPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Store HLS instances in a ref (not state) to avoid re-renders
  // Map: videoKey -> HLS instance
  const hlsInstancesRef = useRef<Map<string, Hls>>(new Map());

  const getHlsInstance = useCallback((videoKey: string): Hls | null => {
    return hlsInstancesRef.current.get(videoKey) || null;
  }, []);

  const setHlsInstance = useCallback((videoKey: string, hls: Hls | null) => {
    if (hls) {
      hlsInstancesRef.current.set(videoKey, hls);
    } else {
      hlsInstancesRef.current.delete(videoKey);
    }
  }, []);

  const destroyHlsInstance = useCallback((videoKey: string) => {
    const hls = hlsInstancesRef.current.get(videoKey);
    if (hls) {
      console.log(`🗑️ [VideoPlayerContext] Destroying HLS instance for video: ${videoKey}`);
      hls.destroy();
      hlsInstancesRef.current.delete(videoKey);
    }
  }, []);

  const clearAllHlsInstances = useCallback(() => {
    console.log(`🗑️ [VideoPlayerContext] Clearing all HLS instances (${hlsInstancesRef.current.size} total)`);
    hlsInstancesRef.current.forEach((hls) => {
      hls.destroy();
    });
    hlsInstancesRef.current.clear();
  }, []);

  return (
    <VideoPlayerContext.Provider
      value={{
        getHlsInstance,
        setHlsInstance,
        destroyHlsInstance,
        clearAllHlsInstances,
      }}
    >
      {children}
    </VideoPlayerContext.Provider>
  );
};

/**
 * Hook to access video player context
 */
export const useVideoPlayer = (): VideoPlayerContextValue => {
  const context = useContext(VideoPlayerContext);
  if (!context) {
    throw new Error('useVideoPlayer must be used within VideoPlayerProvider');
  }
  return context;
};
